import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
      style={{ backgroundImage: "url('/assets/images/FortifiedMindSplash1-1769379659227.jpeg')" }}
    >
      <div className="w-full max-w-4xl bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8 my-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/login" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-600">Last Updated: February 1, 2026</p>
        </div>

        {/* Scrollable Content */}
        <div className="max-h-[600px] overflow-y-auto pr-4 space-y-6 text-gray-700">
          {/* Section 1: Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p className="leading-relaxed">
              Welcome to <strong>Fortified Mind</strong> ("we," "our," or "us"), a non-profit scripture memorization application accessible at <a href="https://fortifiedmind.org" className="text-blue-600 hover:underline">fortifiedmind.org</a>. Our mission is to help individuals worldwide strengthen their faith through scripture memorization and spiritual growth. By accessing or using our services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our application.
            </p>
          </section>

          {/* Section 2: Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Acceptance of Terms</h2>
            <p className="leading-relaxed mb-3">
              By creating an account, accessing our website, or using any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>. These Terms constitute a legally binding agreement between you and Fortified Mind.
            </p>
            <p className="leading-relaxed">
              If you are under 18 years of age, you represent that you have obtained permission from your parent or legal guardian to use our services, and that they have reviewed and agreed to these Terms on your behalf.
            </p>
          </section>

          {/* Section 3: User Accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. User Accounts</h2>
            <p className="leading-relaxed mb-3">
              <strong>Account Creation:</strong> To access certain features of Fortified Mind, you must create an account by providing a valid email address and password. You agree to provide accurate, current, and complete information during registration.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately via our <a href="/contact-us" className="text-blue-600 hover:underline">contact page</a> of any unauthorized use of your account.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Account Termination:</strong> You may delete your account at any time through your account settings. Upon deletion, your personal data will be removed in accordance with our Privacy Policy. We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
            <p className="leading-relaxed">
              <strong>Data Portability:</strong> You have the right to request a copy of your data at any time by <a href="/contact-us" className="text-blue-600 hover:underline">contacting us</a>.
            </p>
          </section>

          {/* Section 4: User Conduct */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. User Conduct</h2>
            <p className="leading-relaxed mb-3">
              You agree to use Fortified Mind in a manner consistent with all applicable laws and regulations. You agree NOT to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the service for any unlawful purpose or to solicit unlawful activity</li>
              <li>Post or transmit content that is harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
              <li>Impersonate any person or entity, or falsely state or misrepresent your affiliation with any person or entity</li>
              <li>Interfere with or disrupt the service or servers or networks connected to the service</li>
              <li>Attempt to gain unauthorized access to any portion of the service, other accounts, or any systems or networks</li>
              <li>Use automated systems (bots, scrapers) to access the service without our express written permission</li>
              <li>Upload or transmit viruses, malware, or any other malicious code</li>
              <li>Collect or harvest any personally identifiable information from other users</li>
            </ul>
          </section>

          {/* Section 5: User-Generated Content */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. User-Generated Content</h2>
            <p className="leading-relaxed mb-3">
              <strong>Content Ownership:</strong> You retain all ownership rights to any content you submit, post, or display on Fortified Mind, including verse reflections, notes, and other user-generated content ("User Content"). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your content solely for the purpose of operating and improving our services.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Content Responsibility:</strong> You are solely responsible for your User Content. We do not endorse, support, represent, or guarantee the completeness, truthfulness, accuracy, or reliability of any User Content. You acknowledge that by using our services, you may be exposed to content that is offensive, harmful, inaccurate, or otherwise inappropriate.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Content Moderation:</strong> While we are not obligated to monitor User Content, we reserve the right (but not the obligation) to remove, edit, or refuse to post any User Content that violates these Terms or that we deem inappropriate, offensive, or harmful.
            </p>
            <p className="leading-relaxed">
              <strong>Liability Disclaimer:</strong> Fortified Mind is not liable for any User Content posted by users. Any reliance you place on User Content is strictly at your own risk. We disclaim all liability arising from User Content, including but not limited to claims of defamation, libel, or infringement.
            </p>
          </section>

          {/* Section 6: Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Intellectual Property</h2>
            <p className="leading-relaxed mb-3">
              <strong>Our Content:</strong> All content, features, and functionality of Fortified Mind, including but not limited to text, graphics, logos, icons, images, audio clips, software, and the compilation thereof ("Our Content"), are the exclusive property of Fortified Mind and are protected by United States and international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Scripture Content:</strong> Biblical texts provided through our service are in the public domain or used under appropriate licenses. We do not claim ownership of scripture content.
            </p>
            <p className="leading-relaxed">
              <strong>Limited License:</strong> We grant you a limited, non-exclusive, non-transferable license to access and use Fortified Mind for personal, non-commercial purposes. You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any of Our Content without our express written permission.
            </p>
          </section>

          {/* Section 7: Religious Content Disclaimer */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Religious Content Disclaimer</h2>
            <p className="leading-relaxed mb-3">
              <strong>Nature of Content:</strong> Fortified Mind provides religious and spiritual content, including scripture from various Bible translations, devotional materials, and faith-based resources. This content is provided for educational, inspirational, and spiritual growth purposes.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>No Professional Advice:</strong> The content provided through Fortified Mind is not intended to be a substitute for professional religious counsel, theological advice, or pastoral care. We encourage users to consult with qualified religious leaders, counselors, or advisors for specific spiritual guidance.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Interpretation:</strong> Users are responsible for their own interpretation and application of religious content. Fortified Mind does not endorse any particular theological interpretation or denominational perspective.
            </p>
            <p className="leading-relaxed">
              <strong>Age-Appropriate Use:</strong> While Fortified Mind is designed to be accessible to users of all ages, parents and guardians are responsible for supervising minors' use of religious content and ensuring it aligns with their family's values and beliefs.
            </p>
          </section>

          {/* Section 8: Third-Party Services */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Third-Party Services</h2>
            <p className="leading-relaxed mb-3">
              Fortified Mind integrates with the following third-party services to provide our functionality:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li><strong>Supabase:</strong> Provides authentication, database, and backend infrastructure</li>
              <li><strong>Resend:</strong> Handles email communications, including newsletters and account notifications</li>
              <li><strong>Stripe:</strong> May be used in the future for processing donations and purchases of materials (if implemented)</li>
              <li><strong>Google Analytics:</strong> May be integrated in the future for usage analytics and service improvement (if implemented)</li>
            </ul>
            <p className="leading-relaxed mb-3">
              <strong>Third-Party Terms:</strong> Your use of these third-party services is subject to their respective terms of service and privacy policies. We are not responsible for the practices, policies, or content of third-party services.
            </p>
            <p className="leading-relaxed">
              <strong>Service Availability:</strong> We rely on third-party services for certain functionality. We are not liable for any disruption, downtime, or data loss resulting from third-party service failures or changes.
            </p>
          </section>

          {/* Section 9: Donations and Non-Profit Status */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Donations and Non-Profit Status</h2>
            <p className="leading-relaxed mb-3">
              <strong>Non-Profit Mission:</strong> Fortified Mind operates as a non-profit organization dedicated to making scripture memorization accessible to all. Our services are provided free of charge to support our mission of spiritual growth and biblical literacy.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Future Donations:</strong> We may implement donation functionality in the future to support the ongoing operation and development of our services. Any donations will be voluntary and used solely to further our non-profit mission.
            </p>
            <p className="leading-relaxed">
              <strong>Material Purchases:</strong> We may offer optional materials (books, study guides, or other resources) for purchase in the future. Any such purchases are entirely optional and not required to use our core services.
            </p>
          </section>

          {/* Section 10: Newsletter Communications */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Newsletter Communications</h2>
            <p className="leading-relaxed mb-3">
              <strong>Newsletter Subscription:</strong> By creating an account, you may have the option to subscribe to our newsletter, which provides updates, spiritual encouragement, scripture memorization tips, and information about new features.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Opt-Out Options:</strong> You may opt out of newsletter communications at any time through:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>Clicking the "Unsubscribe" link at the bottom of any newsletter email</li>
              <li>Updating your communication preferences in your account settings</li>
              <li>Contacting us directly via our <a href="/contact-us" className="text-blue-600 hover:underline">contact page</a></li>
            </ul>
            <p className="leading-relaxed">
              <strong>Transactional Emails:</strong> Please note that opting out of newsletters does not affect transactional emails (account notifications, password resets, security alerts) which are necessary for the operation of your account.
            </p>
          </section>

          {/* Section 11: Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Limitation of Liability</h2>
            <p className="leading-relaxed mb-3">
              <strong>AS-IS Basis:</strong> Fortified Mind is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>No Guarantee:</strong> We do not warrant that our services will be uninterrupted, secure, or error-free, or that defects will be corrected. We do not guarantee the accuracy, completeness, or usefulness of any content provided through our services.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Limitation of Damages:</strong> To the fullest extent permitted by applicable law, Fortified Mind, its directors, employees, volunteers, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>Your access to or use of (or inability to access or use) our services</li>
              <li>Any conduct or content of any third party or user on our services</li>
              <li>Any content obtained from our services</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
            <p className="leading-relaxed">
              <strong>Maximum Liability:</strong> In no event shall our aggregate liability exceed the amount you paid to us (if any) in the twelve (12) months preceding the event giving rise to liability.
            </p>
          </section>

          {/* Section 12: Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Indemnification</h2>
            <p className="leading-relaxed">
              You agree to indemnify, defend, and hold harmless Fortified Mind, its directors, officers, employees, volunteers, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: (a) your use of our services; (b) your violation of these Terms; (c) your violation of any third-party rights, including intellectual property rights or privacy rights; or (d) any User Content you submit, post, or transmit through our services.
            </p>
          </section>

          {/* Section 13: Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Termination</h2>
            <p className="leading-relaxed mb-3">
              <strong>Termination by You:</strong> You may terminate your account at any time by accessing your account settings and selecting the option to delete your account, or by <a href="/contact-us" className="text-blue-600 hover:underline">contacting us</a>.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Termination by Us:</strong> We reserve the right to suspend or terminate your account and access to our services at any time, with or without notice, for any reason, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>Violation of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Extended periods of inactivity</li>
              <li>Requests by law enforcement or government agencies</li>
            </ul>
            <p className="leading-relaxed">
              <strong>Effect of Termination:</strong> Upon termination, your right to use our services will immediately cease. We will delete your personal data in accordance with our Privacy Policy, though some data may be retained as required by law or for legitimate business purposes.
            </p>
          </section>

          {/* Section 14: Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Changes to Terms</h2>
            <p className="leading-relaxed mb-3">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If we make material changes, we will notify you by:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>Posting the updated Terms on our website with a new "Last Updated" date</li>
              <li>Sending an email notification to the address associated with your account</li>
              <li>Displaying a prominent notice within our application</li>
            </ul>
            <p className="leading-relaxed mb-3">
              <strong>Continued Use:</strong> Your continued use of Fortified Mind after any changes to these Terms constitutes your acceptance of the new Terms. If you do not agree to the modified Terms, you must stop using our services and delete your account.
            </p>
            <p className="leading-relaxed">
              <strong>Review Responsibility:</strong> It is your responsibility to review these Terms periodically for changes. We recommend checking this page regularly to stay informed of any updates.
            </p>
          </section>

          {/* Section 15: Governing Law and Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Governing Law and Dispute Resolution</h2>
            <p className="leading-relaxed mb-3">
              <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of the State of Alabama, United States, without regard to its conflict of law provisions.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Jurisdiction:</strong> You agree that any legal action or proceeding arising out of or relating to these Terms or your use of Fortified Mind shall be instituted exclusively in the state or federal courts located in Alabama, and you consent to the personal jurisdiction of such courts.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Dispute Resolution:</strong> In the event of any dispute, claim, or controversy arising out of or relating to these Terms, we encourage you to first <a href="/contact-us" className="text-blue-600 hover:underline">contact us</a> to seek an informal resolution.
            </p>
            <p className="leading-relaxed">
              <strong>Waiver of Class Actions:</strong> You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
          </section>

          {/* Section 16: Severability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">16. Severability</h2>
            <p className="leading-relaxed">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect. The invalid, illegal, or unenforceable provision shall be modified to the minimum extent necessary to make it valid, legal, and enforceable while preserving its intent.
            </p>
          </section>

          {/* Section 17: Entire Agreement */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">17. Entire Agreement</h2>
            <p className="leading-relaxed">
              These Terms, together with our Privacy Policy and any other legal notices or agreements published by us on our services, constitute the entire agreement between you and Fortified Mind concerning your use of our services and supersede all prior agreements, understandings, and communications, whether written or oral.
            </p>
          </section>

          {/* Section 18: Contact Us */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">18. Contact Us</h2>
            <p className="leading-relaxed mb-3">
              If you have any questions, concerns, or feedback regarding these Terms of Service, please contact us:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">Fortified Mind</p>
              <p className="text-gray-700">Email: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us</a></p>
              <p className="text-gray-700">Website: <a href="https://fortifiedmind.org" className="text-blue-600 hover:underline">fortifiedmind.org</a></p>
              <p className="text-gray-700">Location: Alabama, United States</p>
            </div>
            <p className="leading-relaxed mt-4">
              We strive to respond to all inquiries within 48 hours during business days.
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Acknowledgment</h3>
            <p className="leading-relaxed">
              By using Fortified Mind, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. Thank you for being part of our community dedicated to scripture memorization and spiritual growth.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;